import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  IMO,
  buildFormSchemaForCarriers,
  buildPortalContractSchemaForCarriers,
  validateFormData,
  validatePortalSubmissionForm,
  splitFullName,
} from "@aam/shared";
import { useFormStore } from "../store/formStore";
import { FormSection } from "./FormSection";
import { CarrierFieldProvider } from "../context/CarrierFieldContext";
import { createDraft, updateDraft, deleteDraft } from "../hooks/useDraft";
import { useToast } from "../context/ToastContext";
import { FormValidationProvider, useFormValidation } from "../context/FormValidationContext";
import { AutofillProvider, useAutofill, type PortalAutofillPlan } from "../context/AutofillContext";
import { getDynamicSectionIds, PORTAL_FORM_VARIANT } from "../lib/normalizeDraft";
import { Button } from "./ui/Button";
import { ThemeToggle } from "./ui/ThemeToggle";
import { ModalOverlay } from "./ui/Modal";

interface AgentFormProps {
  imo: IMO;
  carrier?: string | null;
  /** Portal omits the public identity step; session supplies identity. */
  variant?: "public" | "portal";
  initialStep?: number;
  draftToken?: string;
  referralCode?: string;
  /** Carriers the agent already has a pending or active contract for — blocked from re-selection. */
  blockedCarriers?: string[];
  /** Carrier slug → why it cannot be picked, e.g. "Pending contract". */
  unavailableCarriers?: Record<string, string>;
  /** Portal only: what this agent's profile and previous contracts can supply. */
  autofill?: PortalAutofillPlan | null;
  onDraftCreated?: (token: string, nextStep: number) => void;
  onStepChange?: (nextStep: number) => void;
  onSubmit: (data: Record<string, unknown>) => Promise<void>;
  onBack: () => void;
}

function formatCarrierName(value: string) {
  return value
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function sectionsEqual(left: string[], right: string[]) {
  if (left.length !== right.length) return false;
  const a = [...left].sort();
  const b = [...right].sort();
  return a.every((value, index) => value === b[index]);
}

function AgentFormInner({
  onSubmit,
  onBack,
  variant = "public",
  initialStep = 0,
  draftToken,
  referralCode,
  blockedCarriers = [],
  unavailableCarriers,
  onDraftCreated,
  onStepChange,
}: Omit<AgentFormProps, "imo" | "carrier" | "autofill">) {
  const isPortal = variant === "portal";
  const carrierNotices = unavailableCarriers ?? {};
  const blockedCarrierList =
    Object.keys(carrierNotices).length > 0 ? Object.keys(carrierNotices) : blockedCarriers;
  const store = useFormStore();
  const autofill = useAutofill();
  const selectedCarriers = (store.formData.selectedCarriers as string[]) ?? [];
  const formSchema = useMemo(
    () =>
      isPortal
        ? buildPortalContractSchemaForCarriers(selectedCarriers)
        : buildFormSchemaForCarriers(selectedCarriers),
    [isPortal, selectedCarriers.join("|")]
  );
  const sections = formSchema.sections;
  const validateAll = isPortal ? validatePortalSubmissionForm : validateFormData;
  const { error: toastError } = useToast();
  const { setFieldErrors, uploadCount } = useFormValidation();

  const [currentStep, setCurrentStep] = useState(initialStep);
  const [direction, setDirection] = useState<1 | -1>(1);
  const [validationSummary, setValidationSummary] = useState<string[]>([]);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [maxStepReached, setMaxStepReached] = useState(initialStep);
  const [carriersAtMaxStep, setCarriersAtMaxStep] = useState<string[]>(selectedCarriers);
  const [pendingCarrierChange, setPendingCarrierChange] = useState<{
    nextCarriers: string[];
    previousCarriers: string[];
  } | null>(null);

  const autoSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isSavingRef = useRef(false);
  const isSubmittingRef = useRef(false);
  const stepHeadingRef = useRef<HTMLDivElement>(null);
  const validationSummaryRef = useRef<HTMLDivElement>(null);

  const goToStep = useCallback((step: number, dir: 1 | -1) => {
    setDirection(dir);
    setCurrentStep(step);
    setMaxStepReached((current) => Math.max(current, step));
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  /**
   * Unlike goToStep, this drops maxStepReached back down to the target step, so
   * a restart also clears the "already completed" state behind it. Without this
   * the stepper still treats every later step as done and lets you jump
   * straight to submit on a form whose answers were just wiped.
   */
  const restartAtStep = useCallback((step: number) => {
    setDirection(-1);
    setCurrentStep(step);
    setMaxStepReached(step);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  const carrierStepIndex = sections.findIndex(
    (section) => section.id === "carriers_to_contract"
  );

  const withPortalVariant = (formData: Record<string, unknown>) =>
    isPortal ? { ...formData, formVariant: PORTAL_FORM_VARIANT } : formData;

  useEffect(() => {
    if (currentStep >= sections.length) {
      goToStep(Math.max(0, sections.length - 1), -1);
    }
  }, [currentStep, goToStep, sections.length]);

  useEffect(() => {
    if (carrierStepIndex >= 0 && maxStepReached > carrierStepIndex) {
      setCarriersAtMaxStep(selectedCarriers);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxStepReached, carrierStepIndex]);

  const currentSection = sections[Math.min(currentStep, sections.length - 1)];
  const showProgress = currentStep > 0;
  const isUploading = uploadCount > 0;

  useEffect(() => {
    if (validationSummary.length > 0) validationSummaryRef.current?.focus();
  }, [validationSummary]);

  // The identity step now collects a single Full Name field. Keep the legacy
  // firstName/lastName values in sync so drafts, submissions, and upstream
  // matching (all of which read those keys) keep working.
  const fullName = typeof store.formData.fullName === "string" ? store.formData.fullName : "";
  useEffect(() => {
    if (!fullName.trim()) return;
    const { firstName, lastName } = splitFullName(fullName);
    const { formData, updateMultipleFields } = useFormStore.getState();
    const updates: Record<string, unknown> = {};
    if (formData.firstName !== firstName) updates.firstName = firstName;
    if (formData.lastName !== lastName) updates.lastName = lastName;
    if (Object.keys(updates).length > 0) updateMultipleFields(updates);
  }, [fullName]);

  useEffect(() => {
    if (currentSection.id !== "tax_information") return;

    const { formData, updateMultipleFields } = useFormStore.getState();
    const updates: Record<string, unknown> = {};
    const nameFromFull =
      typeof formData.fullName === "string" ? formData.fullName.trim() : "";
    const firstName = typeof formData.firstName === "string" ? formData.firstName.trim() : "";
    const lastName = typeof formData.lastName === "string" ? formData.lastName.trim() : "";
    const derivedName = nameFromFull || [firstName, lastName].filter(Boolean).join(" ");

    if (!formData.w9Name && derivedName) {
      updates.w9Name = derivedName;
    }
    if (!formData.w9Address && typeof formData.address === "string" && formData.address.trim()) {
      const line2 = typeof formData.addressLine2 === "string" ? formData.addressLine2.trim() : "";
      updates.w9Address = line2 ? `${formData.address.trim()}, ${line2}` : formData.address.trim();
    }
    if (
      !formData.w9CityStateZip &&
      typeof formData.city === "string" &&
      typeof formData.state === "string" &&
      typeof formData.zipCode === "string" &&
      formData.city.trim() &&
      formData.state.trim() &&
      formData.zipCode.trim()
    ) {
      updates.w9CityStateZip = `${formData.city.trim()}, ${formData.state.trim()} ${formData.zipCode.trim()}`;
    }

    if (Object.keys(updates).length > 0) {
      updateMultipleFields(updates);
    }
  }, [currentSection.id, currentStep]);

  useEffect(() => {
    if (!draftToken || isUploading || submitting) return;

    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(async () => {
      if (isSavingRef.current) return;
      isSavingRef.current = true;
      setSaveStatus("saving");
      try {
        await updateDraft(draftToken, {
          formData: withPortalVariant(useFormStore.getState().formData),
          currentStep,
          email: useFormStore.getState().formData.email as string | undefined,
        });
        setSaveStatus("saved");
        setTimeout(() => setSaveStatus("idle"), 2000);
      } catch {
        setSaveStatus("error");
      } finally {
        isSavingRef.current = false;
      }
    }, 2000);

    return () => {
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    };
  }, [store.formData, draftToken, currentStep, isUploading, submitting]);

  useEffect(() => {
    if (currentSection.id !== "carriers_to_contract") return;
    if (carrierStepIndex < 0 || maxStepReached <= carrierStepIndex) return;
    if (pendingCarrierChange) return;

    const previous = carriersAtMaxStep;
    const next = selectedCarriers;
    const portalOpts = { portal: isPortal };
    if (
      sectionsEqual(
        getDynamicSectionIds(previous, portalOpts),
        getDynamicSectionIds(next, portalOpts)
      )
    ) {
      return;
    }
    if (sectionsEqual(previous, next)) return;

    setPendingCarrierChange({ nextCarriers: next, previousCarriers: previous });
  }, [
    selectedCarriers.join("|"),
    currentSection.id,
    maxStepReached,
    carriersAtMaxStep,
    pendingCarrierChange,
    carrierStepIndex,
    isPortal,
  ]);

  const validateCurrentSection = () => {
    const errors = validateAll(store.formData, selectedCarriers).filter(
      (error) => error.sectionId === currentSection.id
    );
    const fieldErrorMap = Object.fromEntries(errors.map((error) => [error.fieldId, error.message]));
    const summary = errors.map((error) => error.label);

    if (currentSection.id === "carriers_to_contract") {
      const duplicates = selectedCarriers.filter((carrier) =>
        blockedCarrierList.includes(carrier)
      );
      if (duplicates.length > 0) {
        const names = duplicates.map(formatCarrierName).join(", ");
        const message = `You already have a pending or active contract for ${names}. Remove ${
          duplicates.length === 1 ? "it" : "them"
        } to continue — duplicate submissions aren't allowed.`;
        fieldErrorMap.selectedCarriers = message;
        summary.push(message);
      }
    }

    setFieldErrors(fieldErrorMap);
    setValidationSummary(summary);
    return Object.keys(fieldErrorMap).length === 0;
  };

  const persistDraft = async (nextStep: number) => {
    setSaveStatus("saving");
    try {
      if (!draftToken && currentStep === 0) {
        const token = await createDraft({
          formData: withPortalVariant(store.formData),
          currentStep: nextStep,
          email: store.formData.email as string | undefined,
          referralCode,
        });
        setSaveStatus("saved");
        onDraftCreated?.(token, nextStep);
      } else if (draftToken) {
        await updateDraft(draftToken, {
          formData: withPortalVariant(store.formData),
          currentStep: nextStep,
          email: store.formData.email as string | undefined,
        });
        setSaveStatus("saved");
        onStepChange?.(nextStep);
      } else {
        onStepChange?.(nextStep);
      }
      setTimeout(() => setSaveStatus("idle"), 2000);
      return true;
    } catch {
      setSaveStatus("error");
      toastError("Couldn't save your progress. Check your connection and try again.");
      return false;
    }
  };

  const handleNext = async () => {
    if (!validateCurrentSection()) return;
    setValidationSummary([]);

    const nextStep = currentStep + 1;
    const isLast = currentStep === sections.length - 1;

    if (isLast) {
      await handleSubmit();
      return;
    }

    const saved = await persistDraft(nextStep);
    if (!saved) return;

    goToStep(nextStep, 1);
  };

  const handlePrev = () => {
    setValidationSummary([]);
    setFieldErrors({});
    if (currentStep > 0) {
      goToStep(currentStep - 1, -1);
      onStepChange?.(currentStep - 1);
    } else {
      onBack();
    }
  };

  const handleSubmit = async () => {
    if (isSubmittingRef.current) return;

    const submissionData = useFormStore.getState().formData;
    const submissionCarriers = (submissionData.selectedCarriers as string[]) ?? [];
    const errors = validateAll(submissionData, submissionCarriers);
    if (errors.length > 0) {
      setFieldErrors(
        Object.fromEntries(errors.map((error) => [error.fieldId, error.message]))
      );
      setValidationSummary(errors.map((error) => error.message));
      toastError(errors.map((error) => error.message).join("; "));
      return;
    }

    isSubmittingRef.current = true;
    setSubmitting(true);
    try {
      await onSubmit(submissionData);
      if (draftToken) await deleteDraft(draftToken);
      // Submitting in the portal does not appoint anyone, so there is no
      // success screen: the agent goes straight back to their next steps.
      if (isPortal) {
        onBack();
        return;
      }
      setSubmitted(true);
    } catch (err) {
      const fieldErrors = (err as { fieldErrors?: Array<{ fieldId?: string; label?: string; message?: string }> }).fieldErrors;
      if (fieldErrors?.length) {
        setFieldErrors(
          Object.fromEntries(
            fieldErrors.map((field) => [
              field.fieldId ?? field.label ?? "unknown",
              field.message ?? `${field.label ?? "Field"} is required`,
            ])
          )
        );
        setValidationSummary(
          fieldErrors.map(
            (field) => field.message ?? `${field.label ?? "Field"} is required`
          )
        );
      }
      const msg = err instanceof Error ? err.message : "Submission failed";
      toastError(`Submission failed: ${msg}`);
    } finally {
      isSubmittingRef.current = false;
      setSubmitting(false);
    }
  };

  const confirmCarrierChange = () => {
    if (!pendingCarrierChange) return;
    // A carrier change invalidates the contracting requirements, so rather than
    // pruning answers and dropping the agent mid-form, wipe the form and start
    // it over from the first step. Only the new carrier selection survives, so
    // they don't have to re-pick what they just chose. What autofill knew about
    // the agent is still true after a carrier change, so it is re-seeded — the
    // restart clears their answers, not their record.
    const nextFormData = withPortalVariant({
      ...(autofill?.plan?.values ?? {}),
      selectedCarriers: pendingCarrierChange.nextCarriers,
    });
    store.resetForm();
    store.updateMultipleFields(nextFormData);
    setCarriersAtMaxStep(pendingCarrierChange.nextCarriers);
    setPendingCarrierChange(null);
    setFieldErrors({});
    setValidationSummary([]);
    restartAtStep(0);
    onStepChange?.(0);
    if (draftToken) {
      void updateDraft(draftToken, {
        formData: nextFormData,
        currentStep: 0,
        email: undefined,
      });
    }
  };

  const cancelCarrierChange = () => {
    if (!pendingCarrierChange) return;
    store.updateFormData("selectedCarriers", pendingCarrierChange.previousCarriers);
    setPendingCarrierChange(null);
  };

  const isLastStep = currentStep === sections.length - 1;
  const progress = ((currentStep + 1) / sections.length) * 100;

  if (submitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="min-h-[70vh] flex items-center justify-center px-4 py-10"
      >
        <div className="relative w-full max-w-2xl overflow-hidden rounded-card border-2 border-status-active-border bg-status-active-bg p-8 text-center shadow-card sm:p-12">
          <div className="pointer-events-none absolute -bottom-12 -right-10 h-48 w-48 text-status-active-text opacity-10" aria-hidden>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5-4.5A12 12 0 0112 3 12 12 0 014 5.5V11c0 5 3.4 9 8 10 4.6-1 8-5 8-10V5.5z" /></svg>
          </div>
          <div className="relative z-10">
          <div className="w-20 h-20 rounded-card bg-surface-raised border-2 border-status-active-border flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-status-active-text" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p className="mb-3 text-xs font-extrabold uppercase tracking-[0.18em] text-status-active-text">Contract request received</p>
          <h2 className="font-display text-4xl font-extrabold tracking-[-0.045em] text-content-primary mb-3">Contract request submitted</h2>
          <p className="text-content-secondary mb-8 leading-relaxed">
            Your contract request has been received. You'll get a confirmation email shortly.
          </p>
          <Button className="w-full sm:w-auto sm:min-w-64" size="lg" onClick={onBack}>
            Return to contracts
          </Button>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-page">
      <header className="sticky top-0 z-30 border-b-2 border-border-default bg-surface-base/95 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8 flex items-center gap-4">
          <div className="shrink-0 flex items-center gap-2.5 mr-2">
            <div className="w-9 h-9 rounded-card bg-accent-strong flex items-center justify-center">
              <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <span className="leading-tight hidden sm:block">
              <span className="text-sm font-extrabold text-content-primary">Carrier contracting</span><br />
              <span className="text-xs font-medium text-content-muted">Your progress saves automatically</span>
            </span>
          </div>

          <ThemeToggle className="shrink-0" />

          {showProgress && <div className="w-px h-6 bg-border-default shrink-0 hidden sm:block" />}

          {saveStatus !== "idle" && (
            <span className={`text-xs shrink-0 transition-opacity hidden sm:block ${
              saveStatus === "saved" ? "text-status-active-text"
              : saveStatus === "error" ? "text-status-rejected-text"
              : "text-content-muted"
            }`}>
              {saveStatus === "saving" ? "Saving…" : saveStatus === "error" ? "Save failed" : "✓ Draft saved"}
            </span>
          )}

          {currentStep > 0 && (
            <Button variant="ghost" size="sm" onClick={handlePrev} aria-label="Go back" className="shrink-0 px-2">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </Button>
          )}

          {showProgress && (
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-xs text-content-muted font-medium truncate">
                  {currentSection.title}
                </p>
                <p className="text-xs text-content-muted shrink-0 ml-2">
                  {currentStep + 1} / {sections.length}
                </p>
              </div>
              <div className="h-2 bg-surface-muted rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-accent rounded-full"
                  animate={{ width: `${progress}%` }}
                  transition={{ type: "spring", stiffness: 100, damping: 20 }}
                />
              </div>
            </div>
          )}
        </div>

        {showProgress && (
          <div className="max-w-7xl mx-auto px-4 pb-3 sm:px-6 lg:px-8 overflow-x-auto">
            <div className="flex gap-2 min-w-max">
              {sections.map((sec, idx) => (
                <button
                  key={sec.id}
                  type="button"
                  onClick={() => {
                    if (idx < currentStep) {
                      setValidationSummary([]);
                      setFieldErrors({});
                      goToStep(idx, -1);
                      onStepChange?.(idx);
                    }
                  }}
                  className={`px-3 py-2 rounded-lg border text-xs font-bold transition-all whitespace-nowrap ${
                    idx === currentStep
                      ? "border-accent-strong bg-accent-strong text-white"
                      : idx < currentStep
                      ? "border-border-default bg-surface-raised text-content-secondary hover:bg-surface-overlay cursor-pointer"
                      : "border-border-subtle bg-surface-overlay text-content-muted cursor-default"
                  }`}
                >
                  {idx < currentStep ? "✓ " : ""}{sec.title}
                </button>
              ))}
            </div>
          </div>
        )}
      </header>

      <main className="px-4 py-8 pb-24 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              ref={stepHeadingRef}
              tabIndex={-1}
              aria-labelledby="application-step-heading"
              key={currentStep}
              variants={{
                hidden: { opacity: 0, x: direction * 32 },
                visible: { opacity: 1, x: 0 },
                exit: { opacity: 0, x: direction * -32 },
              }}
              initial="hidden"
              animate="visible"
              exit="exit"
              transition={{ duration: 0.2, ease: "easeInOut" }}
              onAnimationComplete={(definition) => {
                if (definition === "visible" && validationSummary.length === 0) {
                  stepHeadingRef.current?.focus();
                }
              }}
            >
              <CarrierFieldProvider value={{ unavailable: carrierNotices }}>
                <FormSection section={currentSection} />
              </CarrierFieldProvider>
            </motion.div>
          </AnimatePresence>

          <AnimatePresence>
            {validationSummary.length > 0 && (
              <motion.div
                ref={validationSummaryRef}
                tabIndex={-1}
                role="alert"
                aria-live="assertive"
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                className="mt-6 p-5 rounded-card bg-status-rejected-bg border-2 border-status-rejected-border"
              >
                <p className="text-sm font-medium text-status-rejected-text mb-1.5">
                  Please complete the following required fields:
                </p>
                <ul className="list-disc list-inside space-y-0.5">
                  {validationSummary.map((err) => (
                    <li key={err} className="text-xs text-status-rejected-text/80">{err}</li>
                  ))}
                </ul>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="sticky bottom-[var(--mobile-nav-offset,0px)] z-20 mt-8 flex gap-3 rounded-card border-2 border-border-default bg-surface-base/95 p-3 shadow-card backdrop-blur-md sm:static sm:ml-auto sm:max-w-xl">
            {currentStep > 0 && (
              <Button variant="secondary" onClick={handlePrev} className="flex-1">
                Previous
              </Button>
            )}
            <Button
              onClick={handleNext}
              disabled={submitting || saveStatus === "saving" || isUploading}
              className="flex-[2] min-h-12"
              data-testid={isLastStep ? "form-submit" : "form-next"}
            >
              {submitting || saveStatus === "saving" ? (
                <>
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  {submitting ? "Submitting…" : "Saving…"}
                </>
              ) : isLastStep ? (
                "Submit Contract Request"
              ) : (
                <>
                  Continue
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </>
              )}
            </Button>
          </div>
        </div>
      </main>

      {pendingCarrierChange && (
        <ModalOverlay onClose={cancelCarrierChange} ariaLabel="Confirm carrier selection change" className="[&>div]:max-w-lg [&>div]:rounded-card [&>div]:border-2 [&>div]:p-6">
            <p className="text-xs uppercase tracking-[0.25em] text-accent mb-2">Carrier selection changed</p>
            <h2 id="carrier-change-title" className="font-display text-2xl font-extrabold text-content-primary mb-2">Restart the remaining contract request?</h2>
            <p className="text-sm text-content-secondary mb-5">
              Changing carriers changes the contracting requirements. To keep your contract request accurate, we'll clear
              every answer and document you've entered and start the form over from the beginning. Your updated carrier
              selection will be kept, so you won't have to pick your carriers again.
            </p>
            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="secondary" onClick={cancelCarrierChange}>
                Keep previous carriers
              </Button>
              <Button onClick={confirmCarrierChange}>
                Clear form and restart
              </Button>
            </div>
        </ModalOverlay>
      )}
    </div>
  );
}

export function AgentForm({ autofill, ...props }: AgentFormProps) {
  return (
    <AutofillProvider plan={autofill ?? null}>
      <FormValidationProvider>
        <AgentFormInner {...props} />
      </FormValidationProvider>
    </AutofillProvider>
  );
}
